import { t } from "i18next";
import { DatePicker } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { ClipSwitch } from "@components/ClipSwitch";
import { twMerge } from "tailwind-merge";

export interface ScheduledDateValue {
  scheduled?: boolean;
  time?: Dayjs | null;
}

export interface ScheduledDateProps {
  className?: string;
  disabled?: boolean;
  value?: ScheduledDateValue;
  error?: boolean;
  errorText?: string;
  onChange?: (value: ScheduledDateValue) => void;
}

const getDateBounds = () => {
  const now = dayjs();
  return {
    minDate: now.startOf("day"),
    maxDate: now.add(7, "day").endOf("day"),
  };
};

export const ScheduledDate = (props: ScheduledDateProps) => {
  const { className, disabled, value, error, errorText, onChange } = props;
  const scheduled = !!value?.scheduled;
  const selectedTime = value?.time ?? null;

  return (
    <div className={twMerge("flex flex-col gap-[4px]", className)}>
      <ClipSwitch
        checked={scheduled}
        disabled={disabled}
        title={t("Schedule Post")}
        onChange={(checked) => {
          if (!checked) {
            onChange?.({ scheduled: false, time: undefined });
            return;
          }
          onChange?.({ scheduled: true, time: selectedTime ?? undefined });
        }}
      />

      {scheduled ? (
        <div className="flex flex-col gap-[4px]">
          <DatePicker
            allowClear
            className="h-[32px] w-[320px] rounded-[8px]"
            placeholder={t("Next 7 days only")}
            disabled={disabled}
            value={selectedTime}
            format="YYYY-MM-DD HH:mm"
            showTime={{
              format: "HH:mm",
              minuteStep: 30,
            }}
            disabledDate={(current) => {
              if (!current) return false;
              const { minDate, maxDate } = getDateBounds();
              return current.isBefore(minDate) || current.isAfter(maxDate);
            }}
            disabledTime={(current) => {
              if (!current) return {};
              const now = dayjs();
              if (!current.isSame(now, "day")) return {};
              const disabledHours = Array.from({ length: now.hour() }, (_, index) => index);
              const disabledMinutes = (selectedHour: number) => {
                if (selectedHour !== now.hour()) return [];
                return Array.from({ length: now.minute() }, (_, index) => index);
              };
              return {
                disabledHours: () => disabledHours,
                disabledMinutes,
              };
            }}
            onChange={(nextValue) => {
              onChange?.({
                scheduled: true,
                time: nextValue ?? undefined,
              });
            }}
          />
          {error ? (
            <div className="text-kds-danger text-[12px] leading-[16px]">
              {t(errorText || "Please select date and time")}
            </div>
          ) : (
            <div className="text-[12px] leading-[16px] text-kds-fg-subtle">{t("Timezone: Eastern Time")}</div>
          )}
        </div>
      ) : null}
    </div>
  );
};
